import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Clock, User, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTimeLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/timelogs`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Error al cargar registros de fichaje');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Group logs by date and sort (memoized)
  const sortedGroupedLogs = useMemo(() => {
    const grouped = logs.reduce((acc, log) => {
      const date = log.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(log);
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => new Date(b) - new Date(a));
  }, [logs]);

  // Calculate total hours per day
  const calculateDailyTotal = (dayLogs) => {
    return dayLogs.reduce((sum, log) => sum + (log.total_hours || 0), 0);
  };

  return (
    <Layout title="Registros de Fichaje">
      <div className="space-y-6 animate-fade-in" data-testid="admin-timelogs">
        <p className="text-slate-400">Visualiza los registros de entrada y salida de todos los técnicos</p>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : logs.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay registros de fichaje</p>
              <p className="text-sm text-slate-500 mt-1">Los fichajes aparecerán aquí cuando los técnicos fichen</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {sortedGroupedLogs
              .map(([date, dayLogs]) => (
                <Card key={date} className="bg-slate-900/50 border-slate-800">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-rajdhani text-lg text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-sky-400" />
                        {formatDate(date)}
                      </CardTitle>
                      <span className="text-sm text-emerald-400 font-medium">
                        Total: {calculateDailyTotal(dayLogs).toFixed(2)}h
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dayLogs.map((log) => (
                        <div
                          key={log.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-sky-500/10 flex items-center justify-center">
                              <User className="w-5 h-5 text-sky-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{log.user_name}</p>
                              <p className="text-sm text-slate-400">
                                Entrada: {formatTime(log.clock_in)} - Salida: {formatTime(log.clock_out)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            {log.total_hours ? (
                              <p className="text-lg font-semibold text-emerald-400">{log.total_hours.toFixed(2)}h</p>
                            ) : (
                              <p className="text-sm text-amber-400">En curso</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
